import PropTypes from 'prop-types';
import classNames from 'classnames';
import React from 'react';
import Spinner from '@mulesoft/anypoint-components/lib/Spinner';
import WarningIcon from '@mulesoft/anypoint-icons/lib/svg/warning-small.svg';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import NewEmptyPage from '@mulesoft/exchange-ui-portals-common/lib/components/EmptyPage';
import PageEditor from '~/components/PageEditor';
import UnderlyingAPITermsSelector from '~/components/UnderlyingAPITermsSelector';
import RelativeLinkTrap from '~/components/common/RelativeLinkTrap';
import { isTermsPage } from '~/utils/page';
import styles from './Page.css';
import EmptyPage from './EmptyPage';

import '@mulesoft/exchange-markdown-editor/lib/styles.css';

class PageComponent extends React.PureComponent {
  static propTypes = {
    isAPIGroup: PropTypes.bool,
    pathToEdit: PropTypes.string,
    page: PropTypes.object,
    canEdit: PropTypes.bool,
    isContentLoading: PropTypes.bool,
    isMarketingSite: PropTypes.bool,
    hasUnsavedChanges: PropTypes.bool,
    actionName: PropTypes.string,
    hasTitle: PropTypes.bool,
    hasUnderlyingTerms: PropTypes.bool,
    isPortalReArchitectureEnabled: PropTypes.bool
  };

  static defaultProps = {
    page: {},
    isMarketingSite: false
  };

  componentDidMount() {
    if (this.props.canEdit) {
      PageEditor.preload();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.canEdit && this.props.canEdit) {
      PageEditor.preload();
    }
  }

  renderEmptyPage() {
    const {
      pathToEdit,
      canEdit,
      hasUnsavedChanges,
      actionName,
      isPortalReArchitectureEnabled,
      page
    } = this.props;

    // if a 422 error was thrown, then it means that asset portals service couldn't parse the markdown and convert it
    // to HTML. Show an error in that case
    if (page?.htmlError?.status === 422) {
      return (
        <div>
          <Icon size="s">
            <WarningIcon />
          </Icon>
          <p>There was an error while loading the portal page</p>
        </div>
      );
    }

    return !isPortalReArchitectureEnabled ? (
      <EmptyPage
        pathToEdit={pathToEdit}
        canEdit={canEdit}
        hasUnsavedChanges={hasUnsavedChanges}
        actionName={actionName}
      />
    ) : (
      <NewEmptyPage
        canEdit={canEdit}
        hasUnsavedChanges={hasUnsavedChanges}
        actionName={actionName}
      />
    );
  }

  renderPageHTML() {
    const { isAPIGroup, page, hasUnderlyingTerms, isMarketingSite } =
      this.props;
    const Wrapper = isMarketingSite ? RelativeLinkTrap : React.Fragment;
    const emptySpan = '<span></span>';

    if (page.targetType === 'text/markdown' && page.html === page.markdown) {
      return (
        <pre className={styles.markdown}>
          <code>{page.markdown}</code>
        </pre>
      );
    }

    return (
      <Wrapper>
        <div
          data-test-id="asset-page-content"
          className={styles.page}
          // workaround for fixing margins on empty terms page
          display-if={page.html !== emptySpan}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: page.html }}
        />
        <UnderlyingAPITermsSelector
          display-if={isTermsPage(page) && isAPIGroup && hasUnderlyingTerms}
        />
      </Wrapper>
    );
  }

  renderSpinner() {
    return (
      <div className={styles.spinner}>
        <Spinner size="l" />
      </div>
    );
  }

  render() {
    const { page, isContentLoading, hasTitle } = this.props;
    let content;

    if (isContentLoading) {
      content = this.renderSpinner();
    } else {
      content = page.html ? this.renderPageHTML() : this.renderEmptyPage();
    }

    return (
      <article
        id="asset-page-content"
        className={styles.assetPage}
        data-test-id="asset-page"
        ref={() => {}}
        aria-labelledby="asset-page-name"
      >
        <div className={styles.header}>
          <h2
            className={classNames(
              { 'visually-hidden': !hasTitle },
              styles.pageName
            )}
            id="asset-page-name"
            data-test-id="asset-page-name"
          >
            {page.name}
          </h2>
        </div>
        <div className={styles.content}>{content}</div>
      </article>
    );
  }
}

const Page = (props) => <PageComponent {...props} />;

export { PageComponent };
export default Page;
